import {
  Brain,
  Cpu,
  ShieldCheck,
  Factory,
  Building2,
  TestTube2,
  Map,
} from "lucide-react";

export const BRAND = {
  name: "NOTION MINDS",
  short: "NMS",
  full: "Notion Minds Systems",
  arabic: "مؤسسة نوش مايندز سيستمز",
  tagline: "Engineering enterprise transformation across Saudi Arabia, Oman and Qatar.",
  logo: "https://customer-assets.emergentagent.com/job_enterprise-transform-6/artifacts/i7xup0t7_NMS_LOGO.png",
  logoArabic: "https://customer-assets.emergentagent.com/job_enterprise-transform-6/artifacts/ckheo700_NMS_LOGO_ARABIC.png",
  icon: "https://customer-assets.emergentagent.com/job_enterprise-transform-6/artifacts/q01vmd76_NMS_ICON_LOGO_FEVICON.png",
};

export const SERVICES = [
  {
    id: "ai-strategy",
    icon: Brain,
    title: "AI Strategy & Feasibility",
    short: "Identify high-value AI opportunities, assess feasibility and ROI, and build practical roadmaps from use case to production.",
    description:
      "Identify high-value AI opportunities, assess feasibility and ROI, and build practical roadmaps from use case to production — grounded in enterprise readiness, responsible AI governance and a measurable path to production value.",
    bullets: [
      "Use case discovery & prioritization",
      "Quantitative ROI & TCO modeling",
      "Enterprise AI readiness scoring",
      "GenAI adoption roadmap (1, 3, 5 yr)",
      "Responsible AI & governance design",
    ],
    span: "col-span-12 md:col-span-8",
    accent: "from-[#d4af37]/20 via-[#d4af37]/5 to-transparent",
  },
  {
    id: "it-consulting",
    icon: Cpu,
    title: "IT Consulting & Digital Engineering",
    short: "Enterprise architecture, modernization, cloud, DevOps and technology transformation designed for scale and resilience.",
    description:
      "Enterprise architecture, modernization, cloud, DevOps and technology transformation designed for scale and resilience — pragmatic engineering across sovereign, hybrid and multi-cloud landscapes with on-demand specialist talent.",
    bullets: [
      "Target-state enterprise architecture",
      "Application & data modernization",
      "Cloud strategy: sovereign, hybrid, multi",
      "DevOps & platform engineering",
      "Staff augmentation & specialist talent",
      "FinOps & operating model design",
    ],
    span: "col-span-12 md:col-span-4",
    accent: "from-[#0055FF]/15 via-[#0055FF]/5 to-transparent",
  },
  {
    id: "cyber-security",
    icon: ShieldCheck,
    title: "Cybersecurity & Resilience",
    short: "Security strategy, governance, architecture, SOC enablement and cyber resilience aligned with Saudi regulatory and enterprise requirements.",
    description:
      "Security strategy, governance, architecture, SOC enablement and cyber resilience aligned with Saudi regulatory and enterprise requirements — NCA, SAMA, NESA and ISO frameworks executed by senior practitioners.",
    bullets: [
      "GRC aligned to NCA, SAMA, NESA, ISO 27001",
      "24x7 SOC enablement & MDR design",
      "Zero-trust & secure architecture",
      "Threat modeling & red teaming",
      "OT-aware incident response",
    ],
    span: "col-span-12 md:col-span-6",
    accent: "from-[#d4af37]/15 via-[#d4af37]/5 to-transparent",
  },
  {
    id: "ot-practice",
    icon: Factory,
    title: "OT & Industrial Cybersecurity",
    short: "Secure IT/OT convergence, industrial environments and critical infrastructure through cybersecurity, engineering and Industry 4.0 capabilities.",
    description:
      "Secure IT/OT convergence, industrial environments and critical infrastructure through cybersecurity, engineering and Industry 4.0 capabilities — Purdue-aligned architectures and IEC 62443 programs without compromising uptime.",
    bullets: [
      "IT-OT convergence reference architecture",
      "ICS / SCADA security assessments",
      "IEC 62443 alignment programs",
      "Industry 4.0 & smart factory enablement",
      "OT-SOC integration & playbooks",
    ],
    span: "col-span-12 md:col-span-6",
    accent: "from-[#0055FF]/15 via-[#0055FF]/5 to-transparent",
  },
  {
    id: "odc-setup",
    icon: Building2,
    title: "ODC & Managed Delivery",
    short: "Establish and operate dedicated technology delivery centers with the right talent, governance, processes and delivery model.",
    description:
      "Establish and operate dedicated technology delivery centers with the right talent, governance, processes and delivery model — sovereign data handling, badge-ready talent, and transparent unit economics.",
    bullets: [
      "ODC operating & governance model",
      "Talent ramp: hire-train-deploy",
      "Sovereign data & compliance posture",
      "Productivity instrumentation",
      "Transition & knowledge transfer",
    ],
    span: "col-span-12 md:col-span-7",
    accent: "from-[#d4af37]/15 via-[#d4af37]/5 to-transparent",
  },
  {
    id: "tcoe",
    icon: TestTube2,
    title: "Quality Engineering & Testing",
    short: "Modernize QA through automation, performance engineering, quality governance and enterprise testing frameworks.",
    description:
      "Modernize QA through automation, performance engineering, quality governance and enterprise testing frameworks — treating quality as an economic outcome across digital, core and OT systems.",
    bullets: [
      "TCoE operating model & charter",
      "Automation framework engineering",
      "Performance & resilience testing",
      "Security & accessibility testing",
      "Quality metrics & defect economics",
    ],
    span: "col-span-12 md:col-span-5",
    accent: "from-[#0055FF]/15 via-[#0055FF]/5 to-transparent",
  },
  {
    id: "gis-mapping",
    icon: Map,
    title: "GIS, Geospatial, Mobile Mapping & Drone Solutions",
    short: "Advanced geospatial solutions combining GIS, mobile mapping, LiDAR, 360° panoramic imaging and drone-based surveying for infrastructure, roads, corridors, assets and large-area mapping.",
    description:
      "Advanced geospatial solutions combining GIS, mobile mapping, LiDAR, 360° panoramic imaging and drone-based surveying for infrastructure, roads, corridors, assets and large-area mapping. Specialized geospatial, mobile mapping and drone solutions delivered through expert technology and execution partners, with Notion Minds managing the engagement end-to-end — from planning and mobilization to execution, quality coordination and final delivery.",
    bullets: [
      "GIS & Geospatial — Geospatial data processing, GIS mapping, spatial analysis and asset visualization.",
      "Mobile Mapping — Vehicle-mounted LiDAR and 360° panoramic imaging for street-level and corridor mapping.",
      "Drone Survey & Aerial Mapping — Drone photogrammetry, aerial LiDAR, orthophotos, 3D terrain models and large-area mapping.",
      "3D LiDAR & Digital Twin — Point clouds, 3D spatial datasets and existing-condition documentation for digital-twin and asset applications.",
    ],
    footnote:
      "The underlying capability material supports aerial LiDAR, drone photogrammetry, RTK GPS, orthophotos, 3D terrain models and dense point clouds.",
    span: "col-span-12",
    accent: "from-[#d4af37]/15 via-[#d4af37]/5 to-transparent",
  },
];

export const INDUSTRIES = [
  {
    id: "banking",
    name: "Banking & Capital Markets",
    blurb:
      "Core modernization, SAMA-aligned cyber, and AI for credit, fraud and customer intelligence.",
  },
  {
    id: "energy",
    name: "Energy & Utilities",
    blurb:
      "OT security, IT-OT convergence, and asset-performance AI for upstream, midstream and grid operators.",
  },
  {
    id: "government",
    name: "Government & Giga-Projects",
    blurb:
      "Sovereign cloud, secure architecture and GenAI accelerators for Vision 2030 programs.",
  },
  {
    id: "retail",
    name: "Retail & Logistics",
    blurb:
      "Composable commerce, demand-sensing AI, and resilient platform engineering at peak.",
  },
];

export const OFFICES = [
  {
    city: "Riyadh",
    country: "Saudi Arabia",
    address: "F-7/S03968, Floor 1, Building 7036, Secondary No. 2487",
    address2: "Saida Street, Durayhmiah District",
    zip: "12796",
    phone: "+966 50 694 7879",
    email: "riyadh@notionminds.sa",
    mapQuery: "Super Office, Saida Street, Durayhmiah District, Riyadh 12796, Saudi Arabia",
  },
];

export const METRICS = [
  { value: "35+", label: "Enterprise engagements" },
  { value: "24×7", label: "Cyber & OT operations" },
  { value: "3 Markets", label: "Saudi Arabia · Oman · Qatar" },
  { value: "End-to-End", label: "Strategy to delivery" },
];

export const PARTNERS = [
  "Microsoft",
  "AWS",
  "Google Cloud",
  "Databricks",
  "ServiceNow",
  "Palo Alto",
  "CrowdStrike",
  "Splunk",
  "OpenAI",
  "NVIDIA",
];
